import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {css} from 'aphrodite';
import Markdown from '../Markdown';

/**
 * TableFooter component for displaying disclaimer text in table footers
 * Handles product-specific lastRefreshedDate logic
 * Connects directly to Redux for lastrefresheddates
 */
// Export the component as a named export for testing purposes
export const TableFooter = ({
  footerColSpan,
  styleSheet,
  selectedProductId,
  lastRefreshedDates,
  disclaimerTemplate
}) => {
  const [productLastRefreshedDate, setProductLastRefreshedDate] = useState('');

  // Define updateProductLastRefreshedDate with useCallback
  const updateProductLastRefreshedDate = useCallback((productId) => {
    let formattedDate = '';
    
    if (lastRefreshedDates?.length && productId) {
      const productRefreshedDate = lastRefreshedDates.find((entry) => entry.productid === productId);

      if (productRefreshedDate?.lastrefresheddate) {
        const event = new Date(productRefreshedDate.lastrefresheddate);
        const options = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          timeZone: 'UTC',
        };
        formattedDate = event.toLocaleDateString('en-US', options);
      }
    }

    setProductLastRefreshedDate(formattedDate);
  }, [lastRefreshedDates]);

  // Update product-specific lastRefreshedDate when selectedProductId changes
  useEffect(() => {
    updateProductLastRefreshedDate(selectedProductId);
  }, [selectedProductId, updateProductLastRefreshedDate]);

  /**
   * Updates the product-specific lastRefreshedDate based on the selected product ID
   * @param {string|number} productId - The ID of the selected product
   * Note: This function is now defined with useCallback above
   */

  // Format the disclaimer with the product-specific date
  const tableDisclaimer = productLastRefreshedDate && disclaimerTemplate
    ? disclaimerTemplate.replace(/\[\[(.*)\]\]/, productLastRefreshedDate)
    : null;

  if (!tableDisclaimer) {
    return null;
  }

  return (
    <tfoot id={'disclaimer'} data-testid="disclaimer-footer">
      <tr>
        <td colSpan={footerColSpan} className={css(styleSheet.footNote)}>
          <Markdown>
            {tableDisclaimer}
          </Markdown>
        </td>
      </tr>
    </tfoot>
  );
};

TableFooter.propTypes = {
  footerColSpan: PropTypes.number.isRequired,
  styleSheet: PropTypes.object.isRequired,
  selectedProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lastRefreshedDates: PropTypes.array,
  disclaimerTemplate: PropTypes.string
};

// Map Redux state to component props
const mapStateToProps = (state) => ({
  lastRefreshedDates: state.coverage.get('lastrefresheddates'),
});

export default connect(mapStateToProps)(TableFooter);
